import { clearSession, getStoredSession, saveSession } from "./session";

const API_URL = (import.meta.env.VITE_API_URL || "/api/v1").replace(/\/$/, "");

export function apiAssetUrl(path: string): string {
  if (!API_URL.startsWith("http")) return path;
  return `${new URL(API_URL).origin}${path}`;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  skipRefresh?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const session = getStoredSession();
  if (!session?.refreshToken) return false;
  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    });
    if (!response.ok) throw new Error("Refresh failed");
    const data = (await response.json()) as {
      accessToken: string;
      refreshToken: string;
    };
    saveSession({ ...session, ...data });
    return true;
  } catch {
    clearSession();
    return false;
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const session = getStoredSession();
  const headers = new Headers(options.headers);
  if (options.body !== undefined)
    headers.set("content-type", "application/json");
  if (session?.accessToken)
    headers.set("authorization", `Bearer ${session.accessToken}`);

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (
    response.status === 401 &&
    !options.skipRefresh &&
    session?.refreshToken &&
    path !== "/auth/refresh"
  ) {
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
    if (await refreshPromise)
      return apiFetch<T>(path, { ...options, skipRefresh: true });
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: { code?: string; message?: string; details?: unknown };
    } | null;
    throw new ApiError(
      response.status,
      payload?.error?.code ?? "REQUEST_FAILED",
      payload?.error?.message ?? "Não foi possível concluir a solicitação.",
      payload?.error?.details,
    );
  }

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") ?? "";
  return contentType.includes("application/json")
    ? (response.json() as Promise<T>)
    : (response.text() as Promise<T>);
}

export function queryString(
  values: Record<string, string | number | undefined>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const localized: Record<string, string> = {
      INVALID_CREDENTIALS: "E-mail ou senha inválidos.",
      ACCOUNT_NOT_ACTIVE: "Sua conta ainda não está ativa.",
      ACCOUNT_TEMPORARILY_LOCKED:
        "Muitas tentativas. Aguarde alguns minutos e tente novamente.",
      EMAIL_ALREADY_REGISTERED: "Este e-mail já possui uma conta.",
      INVALID_VERIFICATION: "Código inválido ou expirado.",
      INVALID_INVITATION: "Este convite é inválido ou expirou.",
      CONTACT_CONSENT_REQUIRED:
        "É necessário autorizar o contato para enviar seus dados.",
      PRIVACY_VERSION_OUTDATED:
        "A política foi atualizada. Recarregue a página para continuar.",
      TERMS_VERSION_OUTDATED:
        "Os termos foram atualizados. Recarregue a página para continuar.",
      LEAD_VERSION_CONFLICT:
        "Este lead foi atualizado por outra pessoa. Recarregue a lista.",
      INSUFFICIENT_POINTS:
        "Você ainda não possui pontos suficientes para esta recompensa.",
      REWARD_OUT_OF_STOCK: "Esta recompensa acabou de esgotar.",
      REFERRAL_SLUG_TAKEN: "Este link de indicação já está em uso.",
      INVALID_REFERRAL_SLUG:
        "Use de 3 a 60 caracteres com letras, números e hífens.",
      RESERVED_REFERRAL_SLUG:
        "Este endereço é reservado. Escolha outro link.",
    };
    return localized[error.code] ?? error.message;
  }
  return error instanceof Error ? error.message : "Ocorreu um erro inesperado.";
}
